import {
    useCallback,
    useRef
} from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import ProfileDropdown from './Components/ProfileDropDown'
import companyInfoQuery from './Pages/Home/services/company-info'
import fetchAuthUserQuery from './common-services/fetch-auth-user'
import companyBillingStatusQuery from './common-services/company-billing-status'
import { hasCompanyAdminAccess } from './helpers/user-check-permissions'

const ENV_TO_COMPANY = {
    QA: "QA Portal",
    ADMIN: "Admin Portal",
    EXECUTIVE: "Executive Portal"
}

const envKey = process.env.REACT_APP_SELECTED_ENV

export default function Header() {
    const menuRef = useRef(null)
    const navigate = useNavigate()

    const { data: company } = useQuery({
        queryKey: ['company'],
        queryFn: companyInfoQuery,
        enabled: envKey === "HR",
    })

    // Company-admin (HR + company_admin scope) gets a Billing entry point in the header.
    const { data: authUser } = useQuery({
        queryKey: ['fetch-auth-user'],
        queryFn: () => fetchAuthUserQuery(),
        enabled: envKey === "HR",
    })

    const showBilling = envKey === "HR" && hasCompanyAdminAccess(authUser?.scopes)

    // Billing health for the header indicator: a dot on the Billing link when the company's billing
    // needs attention (blocked = red, grace/past_due = amber), so a company admin knows to act.
    const { data: billingStatus } = useQuery({
        queryKey: ['company-billing-status'],
        queryFn: () => companyBillingStatusQuery(),
        enabled: showBilling,
    })

    const billingNeedsAttention = billingStatus
        ? (billingStatus.canCreate === false || billingStatus.status === "past_due")
        : false
    const billingBlocked = billingStatus?.canCreate === false

    const companyName = envKey === "HR"
        ? company?.name ?? ''
        : ENV_TO_COMPANY[envKey] ?? ''

    const handleNavigate = useCallback(() => {
        navigate("/")
    }, [navigate])

    return (
        <header className="w-full h-16 bg-white shadow flex items-center justify-between px-8">
            {/* Left: Logo */}
            <div
                className="flex-shrink-0 cursor-pointer"
                onClick={handleNavigate}
                role="button"
                aria-label="Go to homepage"
            >
                <img
                    src="/logo.png"
                    alt="VerifiedID Logo"
                    className="w-40 h-auto hover:opacity-80 transition-opacity duration-200"
                />
            </div>

            {/* Center: Company Name */}
            <div className="text-center mr-[15%] md:mr-[6%] lg:mr-[5%]">
                {companyName && (
                    <span
                        className="text-base sm:text-lg md:text-xl lg:text-2xl font-bold text-gray-700 px-3 py-1 rounded-xl"
                        title={companyName.replace(/\./g, "")}
                    >

                        <span className="block md:hidden">
                            {companyName.replace(/\./g, "").length > 20
                                ? companyName.replace(/\./g, "").slice(0, 20) + "…"
                                : companyName.replace(/\./g, "")}
                        </span>

                        <span className="hidden md:block">
                            {companyName.replace(/\./g, "")}
                        </span>
                    </span>
                )}
            </div>


            <div className="flex items-center gap-4">
                {showBilling && (
                    <button
                        type="button"
                        onClick={() => navigate("/company-billing")}
                        title={billingNeedsAttention
                            ? (billingStatus?.blockReason || billingStatus?.graceWarning || "Billing needs attention")
                            : "Billing"}
                        className="relative flex items-center gap-1.5 text-sm font-semibold text-gray-600 hover:text-blue-600 whitespace-nowrap"
                    >
                        Billing
                        {billingNeedsAttention && (
                            <span
                                aria-label="Billing needs attention"
                                className={`inline-block w-2.5 h-2.5 rounded-full ${billingBlocked ? "bg-red-500" : "bg-amber-500"} animate-pulse`}
                            />
                        )}
                    </button>
                )}
                <div className="relative" ref={menuRef}>
                    <ProfileDropdown />
                </div>
            </div>
        </header>
    )
}
